import { useMemo, useState, type ReactNode } from "react"
import { Flex, Skeleton, Text, Box, Button } from "@chakra-ui/react"
import type { Graph, Timeseries } from "@hydrologiq/schemas/1.0.0/installationPage"
import type { DateTime } from "timezonecomplete"
import TimeSeriesGraphImpl, {
  type AssetConfig,
  type TimeSeriesDataPoint,
  type TimeSeriesSeries,
} from "./TimeSeries"
import { Switch } from "@hydrologiq/shared/chakra/Switch"
import StackedBarBreakdownChart from "./StackedBarBreakdownChart"
import GroupedBarBreakdownChart from "./GroupedBarBreakdownChart"
import { hasPhaseData } from "./PhaseBalanceChart"
import BatteryChart from "./BatteryChart"
import SingleSelectMenu from "./SingleSelectMenu"
import {
  buildAssetOptions,
  filterSeriesToAsset,
  pruneDataToSeries,
  resolveSelectedOption,
} from "./singleAssetSelection"
import type { TimescaleMonitoringGraphPayload } from "../../../hooks/useMonitoringGraphs"

// ─── Shared Utilities ──────────────────────────────────────────────────────

const TIMESCALE_MONITORING_CHART_HEIGHT = "400px"

function scaleDataPointsWToKw(
  data: TimeSeriesDataPoint[] | undefined,
  apply: boolean
): TimeSeriesDataPoint[] | undefined {
  if (!data || !apply) return data
  return data.map((row) => {
    const next: TimeSeriesDataPoint = { ...row }
    for (const key of Object.keys(next)) {
      if (key === "timestamp" || key === "time") continue
      const v = next[key]
      if (typeof v === "number" && Number.isFinite(v)) {
        next[key] = v / 1000
      }
    }
    return next
  })
}

function backfillTimestamps(
  data: TimeSeriesDataPoint[] | undefined,
  sharedTimestamps: string[] | undefined
): TimeSeriesDataPoint[] | undefined {
  if (!data || !sharedTimestamps || sharedTimestamps.length === 0) return data

  const dataMap = new Map(data.map((point) => [point.timestamp as string, point]))

  const valueKeys = new Set<string>()
  for (const point of data) {
    for (const key of Object.keys(point)) {
      if (key === "timestamp" || key === "time") continue
      valueKeys.add(key)
    }
  }

  return sharedTimestamps.map((timestamp) => {
    const existing = dataMap.get(timestamp)
    if (existing) return existing

    const nullPoint: TimeSeriesDataPoint = { timestamp } as TimeSeriesDataPoint
    for (const key of valueKeys) {
      nullPoint[key] = null
    }
    return nullPoint
  })
}

function filterAggregatedPeakSeries(
  data: TimeSeriesDataPoint[],
  series: TimeSeriesSeries<string[]>[],
  showPeaks: boolean,
  isAggregatedView: boolean
): { data: TimeSeriesDataPoint[]; series: TimeSeriesSeries<string[]>[] } {
  if (!isAggregatedView || showPeaks) {
    return { data, series }
  }
  const hasPeakSeries = series.some((s) => s.name === "combinedPeak")
  if (!hasPeakSeries) {
    return { data, series }
  }
  return {
    data: data.map((row) => {
      const { combinedPeak: _p, ...rest } = row as TimeSeriesDataPoint & { combinedPeak?: number | null }
      return rest as TimeSeriesDataPoint
    }),
    series: series.filter((s) => s.name !== "combinedPeak"),
  }
}

function getGraphId(graph: Graph): string {
  const field = graph.source.field
  const ext = graph as Graph & {
    batchGraphId?: string
    filter?: { role?: string; class?: string }
  }
  if (ext.batchGraphId) return ext.batchGraphId
  if (ext.filter?.role) return `${field}__role_${ext.filter.role}`
  if (ext.filter?.class) return `${field}__class_${ext.filter.class}`
  return field
}

const PLACEHOLDER_LINE_COLOR = "#94a3b8"

function buildLinePlaceholderChart(
  axisStart: DateTime,
  axisEnd: DateTime,
  pointCount = 36
): { data: TimeSeriesDataPoint[]; series: TimeSeriesSeries<string[]>[] } {
  const startMs = axisStart.unixUtcMillis()
  const endMs = axisEnd.unixUtcMillis()
  const span = Math.max(1, endMs - startMs)
  const count = Math.max(2, pointCount)
  const data: TimeSeriesDataPoint[] = []
  for (let i = 0; i < count; i++) {
    const ratio = i / (count - 1)
    const ms = Math.round(startMs + ratio * span)
    const iso = new Date(ms).toISOString()
    const wave = 38 + 32 * Math.sin(ratio * Math.PI * 5)
    data.push({ timestamp: iso, value: wave })
  }
  const series: TimeSeriesSeries<string[]>[] = [
    {
      name: "value",
      color: PLACEHOLDER_LINE_COLOR,
      label: "",
      strokeWidth: 2,
      connectNulls: true,
    },
  ]
  return { data, series }
}

const emptyBreakdown = (): {
  data: TimeSeriesDataPoint[]
  series: TimeSeriesSeries<string[]>[]
} => ({ data: [], series: [] })

const ChartLoadingSkeleton = () => (
  <Flex direction="column" gap={2} width="100%" minW={0}>
    <Skeleton height="24px" width="45%" maxW="280px" />
    <Skeleton height={TIMESCALE_MONITORING_CHART_HEIGHT} width="100%" borderRadius="md" />
  </Flex>
)

// ─── Component Interface ───────────────────────────────────────────────────

export interface TimescaleTimeSeriesGraphProps {
  /** Graph definition (title, axes, source config) */
  graph: Graph
  /** Timeseries axis configuration for x-axis rendering */
  timeseries: Timeseries
  /** Start of the visible time range */
  startDate: DateTime
  /** End of the visible time range */
  endDate: DateTime
  /** Optional clamped start for chart axis */
  chartAxisStartDate?: DateTime
  /** Optional clamped end for chart axis */
  chartAxisEndDate?: DateTime
  /** Per-asset display configuration (colors, labels, groups) */
  assetConfigs?: AssetConfig[]
  /** Pre-fetched graph data from the batch monitoring query */
  graphBatchData?: TimescaleMonitoringGraphPayload
  /** Whether the batch query is still loading */
  batchLoading?: boolean
  /** Error from the batch query */
  batchError?: Error
  /** Overlay definitions available for this graph */
  availableOverlays?: Array<{
    graphId: string
    label: string
    yAxisLabel: string
    useSameYAxis?: boolean
  }>
  /** Currently active overlay IDs */
  activeOverlays?: Set<string>
  /** Callback when user toggles an overlay on/off */
  onOverlayToggle?: (overlayId: string, enabled: boolean) => void
  /** When true, renders the BatteryChart variant (SoC + power) */
  batteryView?: boolean
  /** Map of all batch graph payloads (for battery sub-graph lookup) */
  graphsData?: Map<string, TimescaleMonitoringGraphPayload>
  /** Shared timestamps across all graphs for alignment */
  sharedTimestamps?: string[]
  /** Shared Recharts X-axis tick interval across monitoring graphs */
  xAxisTickInterval?: number
  /** Shared X-axis tick positions (UTC ms) for graph alignment */
  xAxisTicksMs?: number[]
  /**
   * Enable the asset-selection dropdown on the grouped-bar (tank level) card.
   * Set by the hybrid parent view (a series per tank category) and the system
   * view (a series per hybrid), so a user can pick which to compare.
   */
  groupedAssetMenu?: boolean
  /**
   * Show eligible graphs (template `singleAssetSelect`, i.e. Battery) one asset at
   * a time behind a dropdown. Off by default so other hosts, such as the hybrid
   * parent view on assets, render exactly as before.
   */
  selectOneAsset?: boolean
  /**
   * Never sum assets visually: graphs flagged `groupedInSystemView` render as
   * side-by-side bars (multi-select menu), and the phase chart draws each ticked
   * asset separately. Off by default so other hosts render exactly as before.
   */
  perAssetView?: boolean
  /** Control for the battery chart's title row; set by the single-select wrapper. */
  headerControl?: ReactNode
}


// ─── Component ─────────────────────────────────────────────────────────────

/**
 * Shared monitoring graph renderer for batch-fetched Timescale data.
 *
 * Handles:
 * - Stacked bar charts (useStackedBarChart template flag)
 * - Battery composite charts (batteryView)
 * - Line/area time series (default)
 * - Overlay lines (Site Load, SoC, etc.)
 * - Peak toggle, breakdown/aggregated switching
 * - W → kW scaling
 * - Shared timestamp backfilling and axis alignment
 *
 * Used by both the system monitoring view and hybrid parent asset monitoring.
 */
const TimescaleTimeSeriesGraphContent = ({
  graph,
  timeseries,
  startDate,
  endDate,
  chartAxisStartDate,
  chartAxisEndDate,
  assetConfigs,
  graphBatchData,
  batchLoading = false,
  batchError,
  availableOverlays,
  activeOverlays,
  onOverlayToggle,
  batteryView = false,
  graphsData,
  sharedTimestamps,
  xAxisTickInterval,
  xAxisTicksMs,
  groupedAssetMenu = false,
  perAssetView = false,
  headerControl,
}: TimescaleTimeSeriesGraphProps) => {
  const field = graph.source.field
  const hasAggregationConfig = (graph as { aggregation?: string }).aggregation === "system"
  const hasBreakdownToggle = (graph as { breakdownToggle?: boolean }).breakdownToggle === true
  const defaultBreakdown = (graph as { defaultBreakdown?: boolean }).defaultBreakdown === true
  const showPeakConfig = (graph as { showPeak?: boolean }).showPeak === true
  const aggregatedValueLabel = (graph as { aggregatedValueLabel?: string }).aggregatedValueLabel
  const useStackedBarChart = (graph as { useStackedBarChart?: boolean }).useStackedBarChart ?? hasBreakdownToggle
  // Grouped (side-by-side) bars for a per-category breakdown (e.g. hybrid tank
  // level: Diesel/Water/Waste). Distinct from the stacked-bar renderer — the
  // categories are not summed. Takes precedence over stacked/line when set.
  const useGroupedBarChart =
    (graph as { useGroupedBarChart?: boolean }).useGroupedBarChart === true ||
    (perAssetView && (graph as { groupedInSystemView?: boolean }).groupedInSystemView === true)

  const [showBreakdown, setShowBreakdown] = useState(defaultBreakdown)
  const [showPeaksAggregated, setShowPeaksAggregated] = useState(false)

  const axisStart = chartAxisStartDate ?? startDate
  const axisEnd = chartAxisEndDate ?? endDate

  const shouldScaleKw =
    graph.source.sourceUnit === "W" && graph.source.desiredUnit === "kW"

  // ─── Data Selection ────────────────────────────────────────────────────

  const batchDisplay = useMemo(() => {
    if (!graphBatchData) return null
    const hasAgg = graphBatchData.hasAggregated
    if (hasAgg && !showBreakdown && graphBatchData.aggregated) {
      return {
        data: graphBatchData.aggregated.data,
        series: graphBatchData.aggregated.series,
      }
    }
    return {
      data: graphBatchData.breakdown.data,
      series: graphBatchData.breakdown.series,
    }
  }, [graphBatchData, showBreakdown])

  const rawData = batchDisplay?.data
  const rawSeries = batchDisplay?.series

  const backfilledData = useMemo(
    () => backfillTimestamps(rawData, sharedTimestamps),
    [rawData, sharedTimestamps]
  )

  const scaledData = useMemo(
    () => scaleDataPointsWToKw(backfilledData, shouldScaleKw),
    [backfilledData, shouldScaleKw]
  )

  const isAggregatedView =
    hasAggregationConfig &&
    !showBreakdown &&
    graphBatchData?.hasAggregated === true

  const { data, series } = useMemo(() => {
    const d = scaledData ?? []
    const s = rawSeries ?? []
    let out = filterAggregatedPeakSeries(d, s, showPeaksAggregated, isAggregatedView)
    if (aggregatedValueLabel && isAggregatedView) {
      out = {
        ...out,
        series: out.series.map((ser) =>
          ser.name === "value" ? { ...ser, label: aggregatedValueLabel } : ser
        ),
      }
    }
    return out
  }, [scaledData, rawSeries, showPeaksAggregated, isAggregatedView, aggregatedValueLabel])

  // ─── Overlay Data ──────────────────────────────────────────────────────

  const overlayData = useMemo(() => {
    if (!graphBatchData?.overlays || !activeOverlays?.size || !availableOverlays?.length) {
      return undefined
    }

    const activeOverlayConfigs = availableOverlays.filter((o) => activeOverlays.has(o.graphId))
    if (activeOverlayConfigs.length === 0) return undefined

    const activeOverlayData = graphBatchData.overlays.filter((o) => activeOverlays.has(o.graphId))
    if (activeOverlayData.length === 0) return undefined

    const overlayConfig = activeOverlayConfigs[0]
    const overlay = activeOverlayData[0]

    // Always prefer the aggregated (summed) data when it exists — an overlay
    // is a single contextual reference line and should not switch to per-asset
    // breakdown lines when the main chart toggles to breakdown mode. Without
    // this, a load overlay with multiple contributors (e.g. VE.Bus output +
    // PV inverter) would render duplicate "Site Load" lines whenever the user
    // turns on the breakdown view. Fall back to breakdown only when no
    // aggregated data was returned (legacy overlays without agg).
    const overlaySource = overlay.aggregated ?? overlay.breakdown

    if (!overlaySource) return undefined

    const backfilledOverlayData = backfillTimestamps(overlaySource.data, sharedTimestamps)
    const scaledOverlayData = scaleDataPointsWToKw(backfilledOverlayData, shouldScaleKw)

    // Use the configured overlay label (e.g. "Site Load") instead of
    // the asset name that comes back from the API breakdown series.
    const labelledSeries = overlaySource.series.map((ser) => ({
      ...ser,
      label: overlayConfig.label ?? ser.label,
    }))

    return {
      data: scaledOverlayData || [],
      series: labelledSeries as typeof overlaySource.series,
      yAxisLabel: overlayConfig.yAxisLabel,
      useSameYAxis: overlayConfig.useSameYAxis,
    }
  }, [graphBatchData, activeOverlays, availableOverlays, shouldScaleKw, sharedTimestamps])

  // ─── Header Controls ───────────────────────────────────────────────────

  const showToggle = hasBreakdownToggle && graphBatchData?.hasAggregated
  const breakdownAssetCount = graphBatchData?.breakdown.series.length ?? 0
  const hideAssetMenuAggregated = isAggregatedView

  const overlayControls = useMemo(() => {
    if (!availableOverlays?.length) return null
    return (
      <Flex alignItems="center" gap={3} flexWrap="wrap">
        {availableOverlays.map((overlay) => (
          <Flex key={overlay.graphId} alignItems="center" gap={2}>
            <Text fontSize="sm" color="fg.muted">
              {overlay.label}
            </Text>
            <Switch
              checked={activeOverlays?.has(overlay.graphId)}
              onCheckedChange={(details) => onOverlayToggle?.(overlay.graphId, details.checked)}
            />
          </Flex>
        ))}
      </Flex>
    )
  }, [availableOverlays, activeOverlays, onOverlayToggle])

  const aggregatedHeaderExtras: ReactNode = useMemo(() => {
    if (!hideAssetMenuAggregated || !showToggle) return null
    return (
      <Flex alignItems="center" gap={3} flexWrap="wrap" justifyContent="flex-end">
        {overlayControls}
        {showPeakConfig && (
          <Flex alignItems="center" gap={2}>
            <Text fontSize="sm" color="fg.muted">
              Show peaks
            </Text>
            <Switch
              checked={showPeaksAggregated}
              onCheckedChange={(details) => setShowPeaksAggregated(details.checked)}
            />
          </Flex>
        )}
        {breakdownAssetCount > 1 && (
          <Button size="sm" variant="hydrologiq_secondary" onClick={() => setShowBreakdown(true)}>
            View asset breakdown
          </Button>
        )}
      </Flex>
    )
  }, [hideAssetMenuAggregated, showToggle, showPeakConfig, showPeaksAggregated, overlayControls, breakdownAssetCount])

  const breakdownHeaderExtras: ReactNode = useMemo(() => {
    if (!showToggle || !showBreakdown || isAggregatedView) return null
    return (
      <Flex alignItems="center" gap={3} flexWrap="wrap" justifyContent="flex-end">
        {overlayControls}
        <Button size="sm" variant="hydrologiq_secondary" onClick={() => setShowBreakdown(false)}>
          View system summary
        </Button>
      </Flex>
    )
  }, [showToggle, showBreakdown, isAggregatedView, overlayControls])

  const linePlaceholder = useMemo(
    () => buildLinePlaceholderChart(axisStart, axisEnd),
    [axisStart, axisEnd]
  )

  // ─── Render ────────────────────────────────────────────────────────────

  if (!field) {
    return (
      <Flex p={4} borderWidth="1px" borderRadius="md" borderColor="red.200" bg="red.50">
        <Text color="red.700">Graph configuration error: missing field in source</Text>
      </Flex>
    )
  }

  // Battery view
  if (batteryView && graphsData) {
    if (batchLoading) return <ChartLoadingSkeleton />
    const baseGraphId = getGraphId(graph)
    const dischargePayload = graphsData.get(`${baseGraphId}_discharge`)
    const chargePayload = graphsData.get(`${baseGraphId}_charge`)
    const socPayload = graphsData.get(`${baseGraphId}_soc`)
    const d = dischargePayload?.breakdown ?? emptyBreakdown()
    const c = chargePayload?.breakdown ?? emptyBreakdown()
    const s = socPayload?.breakdown ?? emptyBreakdown()
    const scaledDischargeData = scaleDataPointsWToKw(d.data, shouldScaleKw)
    const scaledChargeData = scaleDataPointsWToKw(c.data, shouldScaleKw)

    return (
      <BatteryChart
        dischargeData={scaledDischargeData || []}
        dischargeSeries={d.series}
        chargeData={scaledChargeData || []}
        chargeSeries={c.series}
        socData={s.data}
        socSeries={s.series}
        graph={graph}
        chartAxisStartDate={axisStart}
        chartAxisEndDate={axisEnd}
        timeseries={timeseries}
        sharedTimestamps={sharedTimestamps}
        xAxisTickInterval={xAxisTickInterval}
        xAxisTicksMs={xAxisTicksMs}
        headerEndContent={headerControl}
      />
    )
  }

  if (batchLoading) return <ChartLoadingSkeleton />

  if (batchError) {
    return (
      <Flex p={4} borderWidth="1px" borderRadius="md" borderColor="orange.200" bg="orange.50">
        <Text color="orange.700">Failed to load data: {batchError.message}</Text>
      </Flex>
    )
  }

  // Determine rendering mode
  const isBreakdownView =
    (showBreakdown && hasBreakdownToggle) || (defaultBreakdown && !hasBreakdownToggle)
  const shouldUseStackedBar = isBreakdownView && useStackedBarChart
  const allowStackedWithAggregatedOnly =
    shouldUseStackedBar && Boolean(graphBatchData?.aggregated?.data?.length)

  const hasNoSeriesData = !data?.length || !series?.length
  const hasOverlayData = Boolean(overlayData?.data?.length && overlayData?.series?.length)

  // Empty state: placeholder chart.
  // Still render the graph when overlay data exists — a graph can legitimately
  // have no primary series (empty aggregated/breakdown) yet carry an active
  // overlay line (e.g. Site Load on generation) that should be displayed.
  if (hasNoSeriesData && !allowStackedWithAggregatedOnly && !hasOverlayData) {
    return (
      <Box opacity={0.6} width="100%" minW={0}>
        <TimeSeriesGraphImpl
          data={linePlaceholder.data}
          series={linePlaceholder.series}
          graph={graph}
          timeseries={timeseries}
          startDate={axisStart}
          endDate={axisEnd}
          assetConfigs={assetConfigs}
          height={TIMESCALE_MONITORING_CHART_HEIGHT}
          hideAssetMenu
          showLegend={false}
        />
      </Box>
    )
  }

  // Grouped (side-by-side) category bars — e.g. hybrid tank level breakdown.
  // Checked before stacked/line: the per-category series must sit next to each
  // other in each bucket, not stack or draw as lines.
  if (isBreakdownView && useGroupedBarChart) {
    return (
      <GroupedBarBreakdownChart
        data={data}
        series={series}
        graph={graph}
        unit={graph.source?.sourceUnit ?? undefined}
        chartAxisStartDate={axisStart}
        chartAxisEndDate={axisEnd}
        xAxisTicksMs={xAxisTicksMs}
        xAxisTickInterval={xAxisTickInterval}
        showAssetMenu={groupedAssetMenu}
      />
    )
  }

  // Stacked bar chart (Load, Generation breakdown, Fuel)
  if (shouldUseStackedBar) {
    const aggregatedData = graphBatchData?.aggregated
      ? scaleDataPointsWToKw(graphBatchData.aggregated.data, shouldScaleKw)
      : undefined

    const stackedBarPeaksDefaultOff =
      (graph as { stackedBarPeaksDefaultOff?: boolean }).stackedBarPeaksDefaultOff === true
    const stackedBarUseConsumerMenu =
      (graph as { stackedBarUseConsumerMenu?: boolean }).stackedBarUseConsumerMenu === true

    const phaseBalance = graphBatchData?.phaseBalance
    const phaseDataAvailable = phaseBalance ? hasPhaseData(phaseBalance) : false

    return (
      <StackedBarBreakdownChart
        data={data}
        series={series}
        aggregatedData={aggregatedData}
        graph={graph}
        showBackToggle={Boolean(showToggle)}
        onBack={() => setShowBreakdown(false)}
        assetConfigs={assetConfigs}
        overlayData={overlayData}
        showPeakToggle={showPeakConfig}
        defaultShowPeaks={!stackedBarPeaksDefaultOff}
        useConsumerMenu={stackedBarUseConsumerMenu}
        timeseries={timeseries}
        sharedTimestamps={sharedTimestamps}
        xAxisTickInterval={xAxisTickInterval}
        chartAxisStartDate={axisStart}
        chartAxisEndDate={axisEnd}
        xAxisTicksMs={xAxisTicksMs}
        phaseBalanceData={phaseBalance}
        phaseDataAvailable={phaseDataAvailable}
        phasePerAsset={perAssetView}
      />
    )
  }

  // Default: line chart
  return (
    <Box position="relative" width="100%" minW={0}>
      <TimeSeriesGraphImpl
        data={data}
        series={series}
        graph={graph}
        timeseries={timeseries}
        startDate={axisStart}
        endDate={axisEnd}
        assetConfigs={assetConfigs}
        height={TIMESCALE_MONITORING_CHART_HEIGHT}
        hideAssetMenu={hideAssetMenuAggregated}
        headerEndContent={
          hideAssetMenuAggregated ? (aggregatedHeaderExtras ?? undefined) : (breakdownHeaderExtras ?? undefined)
        }
        overlayData={overlayData}
        xAxisTickInterval={xAxisTickInterval}
        sharedTimestamps={sharedTimestamps}
        xAxisTicksMs={xAxisTicksMs}
      />
    </Box>
  )
}

// ─── Single-asset selection wrapper (Battery) ──────────────────────────────

/**
 * Battery sums its batteries into one discharge bar, one charge bar and an
 * averaged SoC line, so there is nothing to multi-select. When the host opts in
 * (`selectOneAsset`) and the graph is eligible (`singleAssetSelect`), this
 * narrows discharge, charge and SoC to one asset chosen from a dropdown.
 * Otherwise it is a pass-through, so existing hosts are unaffected. With fewer
 * than two assets there is nothing to choose and the graph renders as before.
 */
const TimescaleTimeSeriesGraph = ({ selectOneAsset = false, ...props }: TimescaleTimeSeriesGraphProps) => {
  const { graph, graphsData, batchLoading } = props
  const [selectedAsset, setSelectedAsset] = useState<string | null>(null)

  const eligible =
    selectOneAsset &&
    props.batteryView === true &&
    (graph as { singleAssetSelect?: boolean }).singleAssetSelect === true
  const batteryIds = useMemo(() => {
    const base = getGraphId(graph)
    return [`${base}_discharge`, `${base}_charge`, `${base}_soc`]
  }, [graph])

  const options = useMemo(() => {
    if (!eligible || batchLoading) return []
    return buildAssetOptions(batteryIds.map((id) => graphsData?.get(id)?.breakdown.series ?? []))
  }, [eligible, batchLoading, batteryIds, graphsData])

  const selected = resolveSelectedOption(options, selectedAsset)
  const active = eligible && options.length > 1 && selected !== undefined

  const narrowedGraphsData = useMemo(() => {
    if (!active || !selected || !graphsData) return null
    const map = new Map(graphsData)
    for (const id of batteryIds) {
      const payload = map.get(id)
      if (!payload) continue
      const series = filterSeriesToAsset(payload.breakdown.series, selected.value)
      map.set(id, {
        ...payload,
        breakdown: {
          data: pruneDataToSeries(payload.breakdown.data, new Set(series.map((s) => String(s.name)))),
          series,
        },
      })
    }
    return map
  }, [active, selected, batteryIds, graphsData])

  if (!active || !selected || !narrowedGraphsData) {
    return <TimescaleTimeSeriesGraphContent {...props} />
  }

  return (
    <TimescaleTimeSeriesGraphContent
      {...props}
      graphsData={narrowedGraphsData}
      headerControl={
        <SingleSelectMenu
          options={options}
          value={selected.value}
          onChange={setSelectedAsset}
          ariaLabel={`Select asset for ${graph.title ?? "graph"}`}
        />
      }
    />
  )
}

export default TimescaleTimeSeriesGraph
